/**
 * Client mutable state, shared across modules.
 *
 * Everything in here is PRESENTATION. There is no field a module can write that
 * changes an outcome: health, position, cooldowns and the winner arrive from the
 * server and are mirrored here for drawing. If you find yourself wanting to add
 * `player.health = 100` to fix something, the bug is upstream.
 */

import * as THREE from 'three/webgpu';

/** Identity and match context, filled from the room's `world_init`. */
export const session = {
  connected: false,
  matchId: '',
  mapId: '',
  userId: '',
  characterId: 'volt',
  seat: 0,
  tickHz: 30,
  totalTicks: 0,
  arenaRadius: 45,
  seedCommitment: '',
  /** "warmup" | "drop" | "combat" | "resolved" — mirrored from Schema. */
  phase: 'warmup',
};

/** Latest authoritative view of the local player, straight off the snapshot. */
export const localPlayer = {
  mesh: null,
  /** Rendered (interpolated prediction) position. Never written by the network. */
  renderPos: new THREE.Vector3(),
  health: 100,
  maxHealth: 100,
  ultCharge: 0,
  phase: 'alive',
  grounded: true,
  velocityY: 0,
  /** abilityId -> server tick it becomes usable. */
  cooldowns: {},
  effects: [],
};

/** Coarse per-player rows mirrored from Colyseus Schema (never positional). */
export const roster = new Map();

/** Remote character meshes, keyed by player id (decoys included, by design). */
export const remotePlayers = new Map();

export const match = {
  aliveCount: 0,
  timeRemainingMs: 0,
  tick: 0,
  /** Wei, as a string. Formatted only at the UI boundary. */
  potWei: '0',
  zone: { centerX: 0, centerZ: 0, radius: 0, targetRadius: 0, dps: 0 },
  ended: false,
  winnerId: null,
  endReason: null,
};

export const cameraState = {
  yaw: 0,
  pitch: 0.32,
  /**
   * True while a drag-to-look drag is in progress (free camera, or the
   * pointer-lock-refused fallback). InputManager suppresses the LMB attack
   * while it is set — a camera drag must never swing.
   */
  /** Death cam / spectate: detach from the local body. */
  free: false,
  followId: null,
};

export const freeCamPos = new THREE.Vector3(0, 24, 0);

export const cameraShake = {
  intensity: 0,
  duration: 0,
  startTime: 0,
  offset: new THREE.Vector3(),
};

/** Active particle bursts. Owned by vfx/ScreenEffects.js. */
export const particles = [];

/** Frame timing, shared so VFX and the predictor agree on "now". */
export const frame = {
  delta: 1 / 60,
  now: 0,
  /** Non-zero while hitstop is freezing presentation. Never freezes the sim. */
  hitstopUntil: 0,
};
